import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, BookOpen, Bot, Copy,
  Send, Check, Feather,
  Lightbulb, Calendar, Hash, AlertCircle, Loader,
} from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { Card, CardBody } from './ui/Card';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { Input, Textarea } from './ui/Input';
import { supabase } from '../lib/supabaseClient';
import { getSessionUserId } from '../utils/storage';
import { getTotalNAV, getTotalLiabilities, getBucketProgress } from '../utils/calculations';
import { formatVND } from '../utils/format';

// ── Phân loại lỗi Supabase → thông báo tiếng Việt ────────────────────────────
function classifySupabaseError(error: any): string {
  const msg  = (error?.message ?? '').toLowerCase();
  const code = error?.code ?? '';
  if (code === '42501' || msg.includes('row-level security') || msg.includes('permission denied'))
    return 'Lỗi quyền truy cập hoặc phiên làm việc hết hạn. Vui lòng đăng nhập lại.';
  if (code === 'PGRST204' || msg.includes('schema cache') || msg.includes('could not find the'))
    return 'Lỗi Schema Cache — cột không tồn tại trong DB. Kiểm tra cấu trúc bảng.';
  if (msg.includes('jwt') || msg.includes('not authenticated') || String(error?.status) === '401')
    return 'Phiên làm việc hết hạn — vui lòng đăng nhập lại.';
  return `Lỗi lưu dữ liệu: ${error?.message ?? 'Không xác định'}`;
}

// ── Types ──────────────────────────────────────────────────────────────────────
interface Principle {
  id: string;
  title: string;
  content: string;
  color: string;
  display_order: number;
}
interface JournalEntry {
  id: string;
  title: string;
  content: string;
  date: string;
  tags: string[];
  created_at?: string;
}
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
interface ToastState { msg: string; ok: boolean }

// ── Constants ─────────────────────────────────────────────────────────────────
const PRINCIPLE_COLORS = [
  '#38bdf8','#10b981','#fbbf24','#a78bfa',
  '#f87171','#f97316','#84cc16','#ec4899','#06b6d4',
];

const API_KEY = (process.env as any).REACT_APP_ANTHROPIC_API_KEY as string | undefined;

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }) {
  return (
    <div onClick={onDismiss} style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px', borderRadius: 12, cursor: 'pointer',
      background: toast.ok ? 'rgba(16,185,129,0.12)' : 'rgba(248,113,113,0.12)',
      border: `1px solid ${toast.ok ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.3)'}`,
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      backdropFilter: 'blur(12px)',
      animation: 'toastIn 0.2s ease',
    }}>
      <style>{`@keyframes toastIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: toast.ok ? '#10b981' : '#f87171', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {toast.ok
          ? <Check size={11} color="#fff" />
          : <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>!</span>}
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{toast.msg}</span>
    </div>
  );
}

function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  const show = useCallback((msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return { toast, show, dismiss: () => setToast(null) };
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <span style={{ display: 'inline-flex', animation: 'spin 0.8s linear infinite' }}>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
      <Loader size={13} />
    </span>
  );
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
function MarkdownBlock({ text }: { text: string }) {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = (key: string) => {
    if (!listItems.length) return;
    nodes.push(
      <ul key={key} style={{ margin: '8px 0', paddingLeft: 20, listStyle: 'disc' }}>
        {listItems.map((item, i) => (
          <li key={i} style={{ fontSize: 13, color: 'var(--tx-2)', lineHeight: 1.7, marginBottom: 2 }}>
            <Inline text={item} />
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((line, i) => {
    if (/^# (.+)/.test(line))        { flushList(`l${i}`); nodes.push(<h3 key={i} style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx-1)', margin: '18px 0 6px' }}><Inline text={line.slice(2)} /></h3>); }
    else if (/^## (.+)/.test(line))  { flushList(`l${i}`); nodes.push(<h4 key={i} style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx-1)', margin: '14px 0 5px' }}><Inline text={line.slice(3)} /></h4>); }
    else if (/^### (.+)/.test(line)) { flushList(`l${i}`); nodes.push(<h5 key={i} style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-1)', margin: '12px 0 4px' }}><Inline text={line.slice(4)} /></h5>); }
    else if (/^[-•*] (.+)/.test(line)) { listItems.push(line.replace(/^[-•*] /, '')); }
    else if (line.trim() === '')     { flushList(`l${i}`); nodes.push(<div key={i} style={{ height: 8 }} />); }
    else                             { flushList(`l${i}`); nodes.push(<p key={i} style={{ fontSize: 13, color: 'var(--tx-2)', lineHeight: 1.75, margin: '2px 0' }}><Inline text={line} /></p>); }
  });
  flushList('end');
  return <>{nodes}</>;
}

function Inline({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).map((p, i) => {
        if (/^\*\*(.+)\*\*$/.test(p)) return <strong key={i} style={{ color: 'var(--tx-1)', fontWeight: 700 }}>{p.slice(2,-2)}</strong>;
        if (/^\*(.+)\*$/.test(p))     return <em key={i} style={{ color: 'var(--tx-2)', fontStyle: 'italic' }}>{p.slice(1,-1)}</em>;
        if (/^`(.+)`$/.test(p))       return <code key={i} style={{ fontSize: 11, padding: '1px 5px', borderRadius: 4, background: 'var(--bg-card-el)', color: 'var(--tx-2)', fontFamily: 'monospace' }}>{p.slice(1,-1)}</code>;
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

// ── Tab type ──────────────────────────────────────────────────────────────────
type HubTab = 'principles' | 'journal' | 'ai';

// ── Main export ───────────────────────────────────────────────────────────────
export function StrategyHub() {
  const [activeTab, setActiveTab] = useState<HubTab>('principles');
  const tabs: { id: HubTab; icon: React.ReactNode; label: string }[] = [
    { id: 'principles', icon: <BookOpen size={13}/>, label: 'Hiến chương' },
    { id: 'journal',    icon: <Feather size={13}/>,  label: 'Nhật ký' },
    { id: 'ai',         icon: <Bot size={13}/>,       label: 'AI Tư vấn' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '-0.02em' }}>Reflection & Notes</div>
          <div style={{ fontSize: 12, color: 'var(--tx-4)', marginTop: 2 }}>Hiến chương · Nhật ký quản trị · AI Tư vấn</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, padding: 4, borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--bd)', width: 'fit-content' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 10, cursor: 'pointer', border: 'none', fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
              background: activeTab === t.id ? 'var(--bg-app)' : 'transparent',
              color: activeTab === t.id ? 'var(--tx-1)' : 'var(--tx-4)',
              boxShadow: activeTab === t.id ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
            }}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
      {activeTab === 'principles' && <PrinciplesTab />}
      {activeTab === 'journal'    && <JournalTab />}
      {activeTab === 'ai'         && <AiTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Hiến chương
// ─────────────────────────────────────────────────────────────────────────────
function PrinciplesTab() {
  const [items,    setItems]    = useState<Principle[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [addOpen,  setAddOpen]  = useState(false);
  const [editItem, setEditItem] = useState<Principle | null>(null);
  const [form,     setForm]     = useState({ title: '', content: '', color: '#38bdf8' });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [dbError,  setDbError]  = useState('');
  const { toast, show: showToast, dismiss } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('principles').select('*').order('display_order', { ascending: true });
    if (error) setDbError(error.message);
    else { setItems(data ?? []); setDbError(''); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = () => { setAddOpen(false); setEditItem(null); };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const userId = await getSessionUserId();
    if (!userId) {
      showToast('Phiên làm việc hết hạn — vui lòng đăng nhập lại.', false);
      setSaving(false);
      return;
    }
    const payload: Record<string, any> = {
      title:   form.title.trim(),
      content: form.content,
      color:   form.color,
      user_id: userId,
    };
    let error: any;
    if (editItem) {
      ({ error } = await (supabase as any).from('principles').update(payload).eq('id', editItem.id));
    } else {
      ({ error } = await (supabase as any).from('principles').insert({ ...payload, display_order: items.length }));
    }
    setSaving(false);
    if (error) {
      showToast(classifySupabaseError(error), false);
    } else {
      showToast(editItem ? 'Đã cập nhật nguyên tắc!' : 'Đã thêm nguyên tắc mới!', true);
      setForm({ title: '', content: '', color: '#38bdf8' });
      closeModal();
      await load();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Xóa nguyên tắc này?')) return;
    const { error } = await (supabase as any).from('principles').delete().eq('id', id);
    if (error) showToast(`Lỗi xóa: ${error.message}`, false);
    else { showToast('Đã xóa nguyên tắc.', true); await load(); }
  };

  const openEdit = (p: Principle) => {
    setForm({ title: p.title, content: p.content, color: p.color });
    setEditItem(p);
  };

  if (loading) return <LoadingPlaceholder />;

  return (
    <div>
      {toast && <Toast toast={toast} onDismiss={dismiss} />}
      {dbError && <DbErrorBanner msg={dbError} table="principles" />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-2)' }}>Hiến chương WealthOS</div>
          <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 2 }}>{items.length} nguyên tắc cốt lõi định hướng mọi quyết định tài chính</div>
        </div>
        <Button variant="primary" size="sm" icon={<Plus size={13}/>}
          onClick={() => { setForm({ title:'', content:'', color:'#38bdf8' }); setAddOpen(true); }}>
          Thêm nguyên tắc
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={<Lightbulb size={28} color="var(--tx-5)"/>}
          title="Chưa có nguyên tắc nào"
          sub="Thêm các tư duy cốt lõi định hướng chiến lược gia sản của bạn." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 14 }}>
          {items.map(p => {
            const isExp = expanded === p.id;
            return (
              <Card key={p.id}>
                <div style={{ padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: p.color, flexShrink: 0, marginTop: 2 }} />
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx-1)', lineHeight: 1.4 }}>{p.title}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0, marginLeft: 8 }}>
                      <MiniBtn icon={<Edit2 size={11}/>} color="var(--tx-4)" title="Sửa" onClick={() => openEdit(p)} />
                      <MiniBtn icon={<Trash2 size={11}/>} color="#f87171" title="Xóa" onClick={() => handleDelete(p.id)} />
                    </div>
                  </div>
                  {p.content && (
                    <div style={{ cursor: 'pointer' }} onClick={() => setExpanded(isExp ? null : p.id)}>
                      {isExp ? (
                        <div style={{ fontSize: 13, color: 'var(--tx-3)', lineHeight: 1.7 }}>
                          <MarkdownBlock text={p.content} />
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: 'var(--tx-4)', lineHeight: 1.6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>
                          {p.content}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: p.color, marginTop: 8, fontWeight: 600 }}>
                        {isExp ? '▲ Thu gọn' : '▼ Đọc thêm'}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={addOpen || editItem !== null} onClose={closeModal}
        title={editItem ? 'Chỉnh sửa nguyên tắc' : 'Thêm nguyên tắc mới'} size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Tiêu đề *" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
            placeholder="VD: Không đầu tư những gì mình không hiểu" />
          <Textarea label="Nội dung chi tiết (hỗ trợ Markdown)" value={form.content}
            onChange={e => setForm(p => ({...p, content: e.target.value}))}
            placeholder={'**Nguyên tắc:**\n- Điểm 1\n- Điểm 2\n\nÁp dụng thực tế...'}
            style={{ minHeight: 160, fontFamily: 'monospace', fontSize: 12 }} />
          <div>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>Màu nhận diện</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PRINCIPLE_COLORS.map(c => (
                <button key={c} onClick={() => setForm(p => ({...p, color: c}))}
                  style={{ width: 26, height: 26, borderRadius: 7, cursor: 'pointer', background: c, border: `2px solid ${form.color === c ? '#fff' : 'transparent'}`, transform: form.color === c ? 'scale(1.2)' : 'scale(1)', transition: 'all 0.15s' }} />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 14, borderTop: '1px solid var(--bd)' }}>
            <Button variant="ghost" onClick={closeModal} disabled={saving}>Hủy</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}
              icon={saving ? <Spinner/> : undefined}>
              {saving ? 'Đang lưu...' : (editItem ? 'Lưu thay đổi' : 'Thêm')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Nhật ký
// ─────────────────────────────────────────────────────────────────────────────
function JournalTab() {
  const [entries,   setEntries]   = useState<JournalEntry[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [addOpen,   setAddOpen]   = useState(false);
  const [editEntry, setEditEntry] = useState<JournalEntry | null>(null);
  const [viewEntry, setViewEntry] = useState<JournalEntry | null>(null);
  const [dbError,   setDbError]   = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterTag,   setFilterTag]   = useState('');
  const { toast, show: showToast, dismiss } = useToast();

  const emptyForm = () => ({
    title:     '',
    content:   '',
    date:      new Date().toISOString().split('T')[0],
    tagsInput: '',
  });
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('journal_entries').select('*').order('date', { ascending: false });
    if (error) setDbError(error.message);
    else { setEntries(data ?? []); setDbError(''); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const parsedTags = form.tagsInput.split(',').map(t => t.trim()).filter(Boolean);
  const closeModal = () => { setAddOpen(false); setEditEntry(null); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    const userId = await getSessionUserId();
    if (!userId) {
      showToast('Phiên làm việc hết hạn — vui lòng đăng nhập lại.', false);
      setSaving(false);
      return;
    }
    const payload: Record<string, any> = {
      title:   form.title.trim(),
      content: form.content,
      date:    form.date,
      tags:    parsedTags,
      user_id: userId,
    };
    let error: any;
    if (editEntry) {
      ({ error } = await (supabase as any).from('journal_entries').update(payload).eq('id', editEntry.id));
    } else {
      ({ error } = await (supabase as any).from('journal_entries').insert(payload));
    }
    setSaving(false);
    if (error) {
      showToast(classifySupabaseError(error), false);
    } else {
      showToast(editEntry ? 'Đã cập nhật nhật ký!' : 'Đã lưu bài nhật ký mới!', true);
      setForm(emptyForm());
      closeModal();
      await load();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Xóa bài nhật ký này?')) return;
    const { error } = await (supabase as any).from('journal_entries').delete().eq('id', id);
    if (error) showToast(`Lỗi xóa: ${error.message}`, false);
    else {
      showToast('Đã xóa bài nhật ký.', true);
      if (viewEntry?.id === id) setViewEntry(null);
      await load();
    }
  };

  const openEdit = (e: JournalEntry) => {
    setForm({ title: e.title, content: e.content ?? '', date: e.date, tagsInput: (e.tags ?? []).join(', ') });
    setEditEntry(e);
  };

  const allTags    = Array.from(new Set(entries.flatMap(e => e.tags ?? [])));
  const allMonths  = Array.from(new Set(entries.map(e => e.date.slice(0, 7)))).sort().reverse();
  const filtered   = entries.filter(e => {
    const mOk = !filterMonth || e.date.startsWith(filterMonth);
    const tOk = !filterTag   || (e.tags ?? []).includes(filterTag);
    return mOk && tOk;
  });

  if (loading) return <LoadingPlaceholder />;

  return (
    <div>
      {toast && <Toast toast={toast} onDismiss={dismiss} />}
      {dbError && <DbErrorBanner msg={dbError} table="journal_entries" />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-2)' }}>Nhật ký Quản trị</div>
          <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 2 }}>{filtered.length} bài · viết để làm rõ tư duy đầu tư</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 9, fontSize: 12, background: 'var(--bg-card)', border: '1px solid var(--bd)', color: 'var(--tx-2)', cursor: 'pointer', outline: 'none' }}>
            <option value="">Tất cả tháng</option>
            {allMonths.map(m => { const [y,mo] = m.split('-'); return <option key={m} value={m}>T{mo}/{y}</option>; })}
          </select>
          <Button variant="primary" size="sm" icon={<Plus size={13}/>}
            onClick={() => { setForm(emptyForm()); setAddOpen(true); }}>
            Viết mới
          </Button>
        </div>
      </div>

      {allTags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {['', ...allTags].map(tag => (
            <button key={tag || '__all'} onClick={() => setFilterTag(tag)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', transition: 'all 0.1s',
                background: filterTag === tag ? (tag ? '#38bdf820' : 'var(--tx-3)') : 'transparent',
                borderColor: filterTag === tag ? (tag ? '#38bdf8' : 'var(--tx-3)') : 'var(--bd)',
                color: filterTag === tag ? (tag ? '#38bdf8' : 'var(--bg-app)') : 'var(--tx-4)',
              }}>
              {tag ? <><Hash size={9}/>{tag}</> : 'Tất cả'}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={<Feather size={28} color="var(--tx-5)"/>}
          title="Chưa có bài nhật ký nào"
          sub="Ghi lại suy ngẫm sau mỗi quyết định đầu tư quan trọng." />
      ) : (
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          <div style={{ position: 'absolute', left: 8, top: 10, bottom: 10, width: 1, background: 'var(--bd)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {filtered.map(entry => {
              const d = new Date(entry.date + 'T00:00:00');
              const dayLabel = d.toLocaleDateString('vi-VN', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
              const preview = (entry.content ?? '').replace(/#+\s/g,'').replace(/\*\*/g,'').replace(/\*/g,'').slice(0, 180);
              return (
                <div key={entry.id} style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: -22, top: 10, width: 9, height: 9, borderRadius: '50%', background: 'var(--tx-4)', border: '2px solid var(--bg-app)' }} />
                  <Card>
                    <div style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10, color: 'var(--tx-5)', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Calendar size={10}/>{dayLabel}
                            </span>
                            {(entry.tags ?? []).map(tag => (
                              <span key={tag} onClick={() => setFilterTag(tag)}
                                style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: '#38bdf815', color: '#38bdf8', border: '1px solid #38bdf825', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <Hash size={8}/>{tag}
                              </span>
                            ))}
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx-1)', lineHeight: 1.4 }}>{entry.title}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                          <MiniBtn icon={<Edit2 size={11}/>} color="var(--tx-4)" title="Sửa" onClick={() => openEdit(entry)} />
                          <MiniBtn icon={<Trash2 size={11}/>} color="#f87171" title="Xóa" onClick={() => handleDelete(entry.id)} />
                        </div>
                      </div>
                      {preview && (
                        <div onClick={() => setViewEntry(entry)}
                          style={{ cursor: 'pointer', fontSize: 12, color: 'var(--tx-4)', lineHeight: 1.7, marginTop: 6 }}>
                          {preview}{preview.length < (entry.content ?? '').length && '…'}
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#38bdf8', fontWeight: 600 }}>Đọc tiếp →</span>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Read modal */}
      <Modal open={viewEntry !== null} onClose={() => setViewEntry(null)} title={viewEntry?.title ?? ''} size="lg">
        {viewEntry && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--tx-5)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Calendar size={11}/>
                {new Date(viewEntry.date + 'T00:00:00').toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
              </span>
              {(viewEntry.tags ?? []).map(tag => (
                <span key={tag} style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#38bdf815', color: '#38bdf8', border: '1px solid #38bdf825' }}>#{tag}</span>
              ))}
            </div>
            <div style={{ padding: '20px 24px', borderRadius: 12, background: 'var(--bg-app)', border: '1px solid var(--bd)', lineHeight: 1.8, maxHeight: '60vh', overflowY: 'auto' }}>
              <MarkdownBlock text={viewEntry.content ?? ''} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button variant="ghost" icon={<Edit2 size={12}/>} onClick={() => { openEdit(viewEntry); setViewEntry(null); }}>Sửa</Button>
              <Button variant="ghost" onClick={() => setViewEntry(null)}>Đóng</Button>
            </div>
          </>
        )}
      </Modal>

      {/* Write / Edit modal */}
      <Modal open={addOpen || editEntry !== null} onClose={closeModal}
        title={editEntry ? 'Chỉnh sửa bài nhật ký' : 'Viết nhật ký mới'} size="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 12 }}>
            <Input label="Tiêu đề *" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
              placeholder="VD: Quyết định tái cơ cấu danh mục tháng 3" />
            <Input label="Ngày *" type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} />
          </div>

          {/* Tags */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Tags (cách nhau bằng dấu phẩy)</div>
            <input value={form.tagsInput} onChange={e => setForm(p => ({...p, tagsInput: e.target.value}))}
              placeholder="VD: tâm-lý, BĐS, chiến-lược"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 9, fontSize: 12, outline: 'none', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', boxSizing: 'border-box' }} />
            {parsedTags.length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                {parsedTags.map(tag => (
                  <span key={tag} style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#38bdf815', color: '#38bdf8', border: '1px solid #38bdf825' }}>#{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Reflection content */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 11, color: 'var(--tx-4)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Nội dung (Markdown)</div>
              <div style={{ fontSize: 10, color: 'var(--tx-5)' }}>**đậm** · *nghiêng* · # Tiêu đề · - Danh sách</div>
            </div>
            <textarea value={form.content} onChange={e => setForm(p => ({...p, content: e.target.value}))}
              placeholder={'Ghi lại suy ngẫm, quyết định, hoặc bài học hôm nay...\n\n## Bối cảnh\n...\n\n## Quyết định\n...\n\n## Bài học rút ra'}
              style={{ width: '100%', minHeight: 240, padding: '12px 14px', borderRadius: 10, fontSize: 13, fontFamily: 'monospace', lineHeight: 1.7, outline: 'none', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 14, borderTop: '1px solid var(--bd)' }}>
            <Button variant="ghost" onClick={closeModal} disabled={saving}>Hủy</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}
              icon={saving ? <Spinner/> : undefined}>
              {saving ? 'Đang lưu...' : (editEntry ? 'Lưu thay đổi' : 'Lưu nhật ký')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — AI Tư vấn
// ─────────────────────────────────────────────────────────────────────────────
function AiTab() {
  const { state } = useAppState();
  const { assets, buckets, liabilities = [] } = state;
  const totalNAV  = getTotalNAV(assets);
  const totalDebt = getTotalLiabilities(liabilities);
  const netNAV    = totalNAV - totalDebt;
  const realBuckets = buckets.filter(b => b.id !== 'ruong-chua');
  const contextText = buildContextText(assets, buckets, liabilities, totalNAV, totalDebt, netNAV);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input,    setInput]    = useState('');
  const [sending,  setSending]  = useState(false);
  const [copied,   setCopied]   = useState(false);
  const [customQ,  setCustomQ]  = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleCopyAndOpen = async () => {
    const full = customQ.trim()
      ? `${contextText}\n\n---\n\nCâu hỏi của tôi:\n${customQ}`
      : contextText;
    await navigator.clipboard.writeText(full);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    window.open('https://claude.ai/new', '_blank');
  };

  const handleSendApi = async () => {
    if (!input.trim() || sending) return;
    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);
    const systemPrompt = `Bạn là trợ lý tài chính cá nhân WealthOS. Snapshot tài chính:\n\n${contextText}\n\nTrả lời bằng tiếng Việt, ngắn gọn, chuyên nghiệp.`;
    try {
      const resp = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY!, 'anthropic-version': '2023-06-01' },
        body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 1024, system: systemPrompt, messages: [...messages, userMsg].map(m => ({ role: m.role, content: m.content })) }),
      });
      const data = await resp.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.content?.[0]?.text ?? 'Không nhận được phản hồi.' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Không thể kết nối API. Kiểm tra API key và CORS.' }]);
    }
    setSending(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 800 }}>
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-1)' }}>Ngữ cảnh tài chính hiện tại</div>
          <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>Cập nhật real-time từ dữ liệu WealthOS</div>
        </div>
        <CardBody style={{ padding: '14px 20px' }}>
          <div className="rsp-grid-3" style={{ gap: 12, marginBottom: 16 }}>
            <CtxKpi label="Tổng Gia sản" value={formatVND(totalNAV)} color="#10b981" />
            <CtxKpi label="Tổng Nợ" value={formatVND(totalDebt)} color={totalDebt > 0 ? '#f87171' : 'var(--tx-4)'} />
            <CtxKpi label="Tài sản Ròng" value={formatVND(netNAV)} color="#38bdf8" />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {realBuckets.slice(0, 6).map(b => {
              const { nav } = getBucketProgress(b, assets);
              return (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: 'var(--bg-card-el)', border: '1px solid var(--bd)' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: b.color || '#94a3b8' }} />
                  <span style={{ fontSize: 11, color: 'var(--tx-3)', fontWeight: 600 }}>{b.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>{formatVND(nav)}</span>
                </div>
              );
            })}
            {realBuckets.length > 6 && <div style={{ fontSize: 11, color: 'var(--tx-5)', padding: '4px 10px' }}>+{realBuckets.length - 6} quỹ nữa</div>}
          </div>
        </CardBody>
      </Card>

      {API_KEY ? (
        <Card>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bot size={15} color="#a78bfa" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-1)' }}>Claude AI — Tư vấn trực tiếp</div>
              <div style={{ fontSize: 11, color: 'var(--tx-5)' }}>Đặt câu hỏi về chiến lược gia sản</div>
            </div>
          </div>
          <CardBody style={{ padding: 0 }}>
            <div style={{ minHeight: 280, maxHeight: 420, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--tx-5)', fontSize: 12 }}>
                  Hỏi về chiến lược, phân bổ tài sản, hoặc kế hoạch tài chính của bạn.
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '82%', padding: '10px 14px',
                    borderRadius: m.role === 'user' ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
                    background: m.role === 'user' ? '#a78bfa20' : 'var(--bg-card-el)',
                    border: `1px solid ${m.role === 'user' ? '#a78bfa30' : 'var(--bd)'}`,
                  }}>
                    {m.role === 'assistant'
                      ? <div style={{ fontSize: 13, lineHeight: 1.7 }}><MarkdownBlock text={m.content} /></div>
                      : <div style={{ fontSize: 13, color: 'var(--tx-2)', lineHeight: 1.6 }}>{m.content}</div>}
                  </div>
                </div>
              ))}
              {sending && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--tx-5)', fontSize: 12 }}>
                  <Spinner /><span>Claude đang trả lời...</span>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--bd)', display: 'flex', gap: 8 }}>
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendApi(); } }}
                placeholder="Hỏi về chiến lược, rủi ro, phân bổ tài sản..."
                style={{ flex: 1, padding: '9px 12px', borderRadius: 10, fontSize: 13, outline: 'none', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)' }} />
              <button onClick={handleSendApi} disabled={!input.trim() || sending}
                style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: (sending || !input.trim()) ? 'var(--bd)' : '#a78bfa', border: 'none', cursor: (sending || !input.trim()) ? 'default' : 'pointer', transition: 'background 0.15s' }}>
                {sending ? <Spinner /> : <Send size={14} color="#fff" />}
              </button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bot size={15} color="#a78bfa" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-1)' }}>Mở Claude với Ngữ cảnh</div>
              <div style={{ fontSize: 11, color: 'var(--tx-5)' }}>Copy snapshot → mở Claude.ai → dán và hỏi</div>
            </div>
          </div>
          <CardBody style={{ padding: '20px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--tx-4)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Câu hỏi của bạn (tùy chọn)</div>
              <textarea value={customQ} onChange={e => setCustomQ(e.target.value)}
                placeholder="VD: Danh mục của tôi có phân bổ hợp lý chưa? Tôi nên ưu tiên đầu tư thêm vào quỹ nào?"
                style={{ width: '100%', minHeight: 80, padding: '10px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.6, outline: 'none', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--tx-4)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Nội dung sẽ được copy</div>
              <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-app)', border: '1px solid var(--bd)', maxHeight: 200, overflowY: 'auto' }}>
                <pre style={{ fontSize: 11, color: 'var(--tx-4)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {customQ.trim() ? `${contextText}\n\n---\n\nCâu hỏi của tôi:\n${customQ}` : contextText}
                </pre>
              </div>
            </div>
            <button onClick={handleCopyAndOpen}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, cursor: 'pointer', border: 'none', fontSize: 13, fontWeight: 700,
                background: copied ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#a78bfa,#7c3aed)',
                color: '#fff', transition: 'background 0.3s',
              }}>
              {copied ? <Check size={15}/> : <Copy size={15}/>}
              {copied ? 'Đã copy! Đang mở Claude.ai...' : 'Copy Ngữ cảnh & Mở Claude.ai'}
            </button>
            <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.15)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Bot size={14} color="#a78bfa" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 11, color: 'var(--tx-4)', lineHeight: 1.7 }}>
                <strong style={{ color: 'var(--tx-2)' }}>Muốn chat trực tiếp?</strong> Thêm{' '}
                <code style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--bg-card-el)', color: '#a78bfa' }}>REACT_APP_ANTHROPIC_API_KEY=sk-ant-...</code>{' '}
                vào <code style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--bg-card-el)', color: 'var(--tx-3)' }}>.env</code> rồi khởi động lại app.
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ── Context builder ───────────────────────────────────────────────────────────
function buildContextText(assets: any[], buckets: any[], liabilities: any[], totalNAV: number, totalDebt: number, netNAV: number) {
  const realBuckets = buckets.filter((b: any) => b.id !== 'ruong-chua');
  const lines: string[] = [
    '# SNAPSHOT TÀI CHÍNH — WealthOS',
    `Ngày: ${new Date().toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}`,
    '',
    '## CHỈ SỐ VĨ MÔ',
    `- Tổng gia sản: ${formatVND(totalNAV)}`,
    `- Tổng nợ: ${formatVND(totalDebt)}`,
    `- Tài sản ròng (Net NAV): ${formatVND(netNAV)}`,
    `- Tỷ lệ nợ: ${totalNAV > 0 ? ((totalDebt/totalNAV)*100).toFixed(1) : 0}%`,
    '', '## PHÂN BỔ THEO QUỸ',
  ];
  realBuckets.forEach((b: any) => {
    const nav = assets.filter((a: any) => a.bucketId === b.id).reduce((s: number, a: any) => s + a.currentPrice * a.quantity, 0);
    const pct = totalNAV > 0 ? ((nav/totalNAV)*100).toFixed(1) : 0;
    lines.push(`- ${b.name}: ${formatVND(nav)} (${pct}%)${b.targetValue > 0 ? `  MT: ${formatVND(b.targetValue)}` : ''}`);
  });
  lines.push('', '## TOP 10 TÀI SẢN');
  [...assets].sort((a, b) => (b.currentPrice*b.quantity) - (a.currentPrice*a.quantity)).slice(0, 10)
    .forEach((a: any, i: number) => {
      const fund = buckets.find((b: any) => b.id === a.bucketId)?.name ?? 'Chưa phân bổ';
      lines.push(`${i+1}. [${a.code||a.id}] ${a.name} — ${formatVND(a.currentPrice*a.quantity)} · ${a.assetClass} · ${fund}`);
    });
  if (assets.length > 10) lines.push(`   ... và ${assets.length-10} tài sản khác`);
  if (liabilities.length > 0) {
    lines.push('', '## KHOẢN NỢ');
    liabilities.forEach((l: any) => lines.push(`- ${l.name}: Dư nợ ${formatVND(l.outstandingBalance)} · Lãi ${l.interestRate}%/năm`));
  }
  return lines.join('\n');
}

// ── Shared components ─────────────────────────────────────────────────────────
function CtxKpi({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--bg-app)', border: '1px solid var(--bd)' }}>
      <div style={{ fontSize: 10, color: 'var(--tx-5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color, letterSpacing: '-0.02em' }}>{value}</div>
    </div>
  );
}

function MiniBtn({ icon, color, title, onClick }: { icon: React.ReactNode; color: string; title?: string; onClick: () => void }) {
  return (
    <button onClick={onClick} title={title}
      style={{ width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-card-el)', border: 'none', color, transition: 'background 0.1s' }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-el)'; }}>
      {icon}
    </button>
  );
}

function EmptyState({ icon, title, sub }: { icon: React.ReactNode; title: string; sub: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 0', border: '1.5px dashed var(--bd)', borderRadius: 16 }}>
      <div style={{ margin: '0 auto 12px', display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-3)', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--tx-5)' }}>{sub}</div>
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[1,2,3].map(i => <div key={i} style={{ height: 80, borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--bd)', opacity: 0.5 }} />)}
    </div>
  );
}

function DbErrorBanner({ msg, table }: { msg: string; table: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px', borderRadius: 12, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', marginBottom: 16 }}>
      <AlertCircle size={14} color="#f87171" style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ fontSize: 12, color: 'var(--tx-3)', lineHeight: 1.6 }}>
        <strong style={{ color: '#f87171' }}>Lỗi kết nối bảng:</strong> {msg}
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 11, color: '#38bdf8', fontWeight: 600 }}>Xem SQL tạo bảng</summary>
          <pre style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-app)', fontSize: 10, color: 'var(--tx-3)', lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
            {table === 'principles' ? CREATE_PRINCIPLES_SQL : CREATE_JOURNAL_SQL}
          </pre>
        </details>
      </div>
    </div>
  );
}

const CREATE_PRINCIPLES_SQL = `CREATE TABLE principles (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  user_id uuid,
  title text NOT NULL DEFAULT '',
  content text NOT NULL DEFAULT '',
  color text DEFAULT '#38bdf8',
  display_order int DEFAULT 0,
  created_at timestamptz DEFAULT now()
);
ALTER TABLE principles ENABLE ROW LEVEL SECURITY;
CREATE POLICY principles_user ON principles FOR ALL USING (user_id = auth.uid()) WITH CHECK (user_id = auth.uid());`;

const CREATE_JOURNAL_SQL = `CREATE TABLE journal_entries (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  user_id uuid,
  title text NOT NULL DEFAULT '',
  content text NOT NULL DEFAULT '',
  date date NOT NULL DEFAULT CURRENT_DATE,
  tags text[] DEFAULT '{}',
  created_at timestamptz DEFAULT now()
);
ALTER TABLE journal_entries ENABLE ROW LEVEL SECURITY;
CREATE POLICY journal_user ON journal_entries FOR ALL USING (user_id = auth.uid()) WITH CHECK (user_id = auth.uid());`;
